/**
 * Состояние экранов приложения.
 *
 * Переходы описаны явным типом: невозможно оказаться на экране результата без
 * результата, потому что данные экрана — часть его состояния.
 *
 * sessionId создаётся в момент перехода и хранится в состоянии экрана. Если бы
 * он создавался при отрисовке, каждый рендер начинал бы новую сессию набора.
 */

import type { LanguageCode, SessionId, TypingMetrics } from '@type2x/shared-contracts';
import type { DiagnosticResult, DiagnosticTask } from '@type2x/feature-diagnostics';
import type { PracticePlan } from '@type2x/feature-adaptive-practice';
import type { ControlSubmission, ControlTask } from '@type2x/feature-control-test';
import type { MistakesSession } from '@type2x/feature-my-mistakes';
import type { SentencesSession } from '@type2x/feature-sentence-practice';
import type { FundamentalsPlan } from '@type2x/feature-fundamentals';
import type { SymbolMode, SymbolsPlan } from '@type2x/feature-symbols';
import type { PackMode, PacksPlan } from '@type2x/feature-packs';
import type { LayoutSwitchPlan } from '@type2x/feature-layout-switch';
import type { TempoMode, TempoSession } from '@type2x/feature-tempo';
import type { CustomTextPlan } from '@type2x/feature-custom-text';
import type { AchievementId } from '@type2x/feature-personal-workspace';
import type { SessionOutcomeSummary, SkillReport } from '@type2x/feature-progress';
import type { AchievementsView } from './achievements.js';

/**
 * Режим, которым получено занятие.
 *
 * Экран результата один на все режимы, и до этого он не знал, откуда пришёл:
 * «ещё одна тренировка» вела в основную колею независимо от того, что человек
 * только что делал. Закончивший «Предложения» получал упражнение на три буквы
 * — и кнопка обещала не то, куда вела.
 *
 * Режимы с вариантами носят свой вариант: «Цифры» и «Пунктуация» — разные
 * занятия, и повторять надо то, которое было.
 */
export type PractisedMode =
  | { readonly kind: 'practice' }
  | { readonly kind: 'fundamentals' }
  | { readonly kind: 'sentences' }
  | { readonly kind: 'mistakes' }
  | { readonly kind: 'symbols'; readonly mode: SymbolMode }
  | { readonly kind: 'packs'; readonly mode: PackMode }
  | { readonly kind: 'tempo'; readonly mode: TempoMode }
  | { readonly kind: 'layout-switch' }
  | { readonly kind: 'custom-text' };

/** Экран приложения вместе с его данными. */
export type AppScreen =
  | { readonly name: 'loading' }
  | { readonly name: 'onboarding' }
  /**
   * Главная: откуда видно все режимы и куда возвращает «Назад».
   *
   * Своих данных у неё нет: всё, что она показывает, берётся из пространства.
   */
  | { readonly name: 'home' }
  | { readonly name: 'diagnostics'; readonly task: DiagnosticTask; readonly sessionId: SessionId }
  /**
   * Итог диагностики: уровень, слабые места и что делать дальше.
   *
   * Отдельный экран, а не часть тренировки: измерение закончилось, и человеку
   * есть что о нём сказать до того, как начнётся занятие.
   */
  | { readonly name: 'diagnostic-result'; readonly diagnostic: DiagnosticResult }
  /**
   * Рейтинг: таблица лидеров языка тренировки.
   *
   * Своих данных у экрана нет: снимок он спрашивает у сервера сам, потому что
   * рейтинг считается там и к моменту открытия уже другой.
   */
  | { readonly name: 'leaderboard' }
  /** Достижения, серия занятий и личные рекорды. */
  | { readonly name: 'achievements'; readonly view: AchievementsView }
  | {
      readonly name: 'practice';
      readonly plan: PracticePlan;
      readonly sessionId: SessionId;
    }
  | {
      readonly name: 'my-mistakes';
      readonly session: MistakesSession;
      readonly sessionId: SessionId;
    }
  | {
      readonly name: 'sentences';
      readonly session: SentencesSession;
      readonly sessionId: SessionId;
    }
  | {
      readonly name: 'fundamentals';
      readonly plan: FundamentalsPlan;
      readonly sessionId: SessionId;
    }
  | {
      readonly name: 'symbols';
      readonly plan: SymbolsPlan;
      readonly sessionId: SessionId;
    }
  | {
      readonly name: 'packs';
      readonly plan: PacksPlan;
      readonly sessionId: SessionId;
    }
  | {
      readonly name: 'layout-switch';
      readonly plan: LayoutSwitchPlan;
      readonly sessionId: SessionId;
    }
  | {
      readonly name: 'tempo';
      readonly session: TempoSession;
      readonly sessionId: SessionId;
    }
  | {
      readonly name: 'custom-text';
      /** Занятие; null — пользователь ещё не принёс текст. */
      readonly plan: CustomTextPlan | null;
      /** Текст принесён, но слов в нём слишком мало. */
      readonly tooShort: boolean;
      readonly sessionId: SessionId;
    }
  | {
      readonly name: 'result';
      readonly summary: SessionOutcomeSummary;
      /** Чем занимались: по нему «ещё раз» знает, что повторять. */
      readonly practised: PractisedMode;
      /**
       * Рубежи, взятые этим занятием.
       *
       * Момент получения — единственный, когда о достижении есть что сказать:
       * на экране достижений оно уже просто стоит в списке (ТЗ FR-GM-07).
       */
      readonly earned: readonly AchievementId[];
    }
  | { readonly name: 'report'; readonly report: SkillReport }
  | {
      readonly name: 'control-test';
      readonly task: ControlTask;
      readonly sessionId: SessionId;
    }
  | {
      readonly name: 'control-result';
      readonly outcome: ControlSubmission | null;
      /**
       * Метрики прогона.
       *
       * Они известны сразу и от ответа сервера не зависят: вердикт решает,
       * идёт ли результат в рейтинг, а не что человек набрал.
       */
      readonly metrics: TypingMetrics;
    }
  | { readonly name: 'account' }
  /**
   * Первый вход в новый язык тренировки.
   *
   * Спрашивает то же, что и первый вход, кроме самого языка: он уже выбран, и
   * спрашивать его второй раз значит не помнить, о чём был предыдущий экран.
   */
  | { readonly name: 'language-setup'; readonly language: LanguageCode }
  /**
   * Объяснение закрытого режима вместо самого режима.
   *
   * Нажатие на закрытый режим ведёт сюда, а не в никуда: кнопка, которая не
   * отвечает, читается как сломанная, а не как запертая.
   */
  | { readonly name: 'locked-mode'; readonly target: LockedTarget }
  /** Смена пароля по ссылке из письма. */
  | { readonly name: 'password-reset'; readonly token: string };

/**
 * Режим, который пользователь пытался открыть, не выполнив условия.
 *
 * Хранится именно намерение, а не готовое занятие: занятие соберётся, только
 * если пользователь решит всё равно попробовать.
 */
export type LockedTarget =
  { readonly kind: 'packs'; readonly mode: PackMode } | { readonly kind: 'layout-switch' };

/** Начальный экран до загрузки локального пространства. */
export const INITIAL_SCREEN: AppScreen = { name: 'loading' };
